import { Controller } from "@hotwired/stimulus"
import {
  addDays,
  addMonths,
  calendarDays,
  formatDisplayDate,
  formatDisplayTime,
  startOfMonth,
  toIsoDate,
  weekdayLabels
} from "../shared/date_picker_helpers"

export default class extends Controller {
  static targets = [
    "dateSegment",
    "dateInput",
    "dateLabel",
    "dateClear",
    "dateDropdown",
    "dateQuick",
    "dateCalendar",
    "timeSegment",
    "timeInput",
    "timeLabel",
    "timeClear",
    "timeDropdown",
    "timeQuick",
    "timeGrid"
  ]

  static values = {
    anyDate: String,
    today: String,
    tomorrow: String,
    pickDate: String,
    anyTime: String,
    pickTime: String
  }

  connect() {
    this.calendarView = this.startOfMonthFromInput()
    this.syncDateFromInput()
    this.syncTimeFromInput()
    this.boundDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.boundDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundDocumentClick)
  }

  prepareSubmit() {
    if (!this.dateInputTarget.value) this.dateInputTarget.disabled = true
    if (!this.timeInputTarget.value) this.timeInputTarget.disabled = true
    this.closeAllDropdowns()
  }

  handleDocumentClick(event) {
    if (this.element.contains(event.target)) return
    this.closeAllDropdowns()
  }

  toggleDatePicker(event) {
    if (event.target.closest(".hero-search__date-clear")) return
    if (event.target.closest(".hero-search__dropdown--date")) return

    event.preventDefault()
    this.closeTimeDropdown()

    if (this.dateDropdownTarget.classList.contains("hidden")) {
      this.openDateDropdown()
    } else {
      this.closeDateDropdown()
    }
  }

  toggleTimePicker(event) {
    if (event.target.closest(".browse-schedule-picker__clear")) return
    if (event.target.closest(".hero-search__dropdown--time")) return

    event.preventDefault()
    this.closeDateDropdown()

    if (this.timeDropdownTarget.classList.contains("hidden")) {
      this.openTimeDropdown()
    } else {
      this.closeTimeDropdown()
    }
  }

  openDateDropdown() {
    this.dateDropdownTarget.classList.remove("hidden")
    this.dateSegmentTarget.classList.add("hero-search__segment--dropdown-open")
    this.renderDateDropdown()
  }

  closeDateDropdown() {
    this.dateDropdownTarget.classList.add("hidden")
    this.dateSegmentTarget.classList.remove("hero-search__segment--dropdown-open")
  }

  openTimeDropdown() {
    this.timeDropdownTarget.classList.remove("hidden")
    this.timeSegmentTarget.classList.add("hero-search__segment--dropdown-open")
    this.renderTimeDropdown()
  }

  closeTimeDropdown() {
    this.timeDropdownTarget.classList.add("hidden")
    this.timeSegmentTarget.classList.remove("hero-search__segment--dropdown-open")
  }

  closeAllDropdowns() {
    this.closeDateDropdown()
    this.closeTimeDropdown()
  }

  clearDate(event) {
    event.preventDefault()
    event.stopPropagation()
    this.setAnyDate()
  }

  clearTime(event) {
    event.preventDefault()
    event.stopPropagation()
    this.setAnyTime()
  }

  syncDateFromInput() {
    const value = this.dateInputTarget.value
    if (value) {
      this.dateInputTarget.disabled = false
      this.dateLabelTarget.textContent = formatDisplayDate(value)
      this.dateClearTarget.classList.remove("hidden")
    } else {
      this.setAnyDate()
    }
  }

  syncTimeFromInput() {
    const value = this.timeInputTarget.value
    if (value) {
      this.timeInputTarget.disabled = false
      this.timeLabelTarget.textContent = formatDisplayTime(value)
      this.timeClearTarget.classList.remove("hidden")
    } else {
      this.setAnyTime()
    }
  }

  setAnyDate() {
    this.dateInputTarget.value = ""
    this.dateInputTarget.disabled = true
    this.dateLabelTarget.textContent = this.anyDateValue
    this.dateClearTarget.classList.add("hidden")
  }

  setAnyTime() {
    this.timeInputTarget.value = ""
    this.timeInputTarget.disabled = true
    this.timeLabelTarget.textContent = this.anyTimeValue
    this.timeClearTarget.classList.add("hidden")
  }

  renderDateDropdown() {
    this.renderDateQuickPicks()
    this.renderCalendar()
  }

  renderDateQuickPicks() {
    const selected = this.dateInputTarget.value
    const todayIso = toIsoDate(new Date())
    const tomorrowIso = toIsoDate(addDays(new Date(), 1))

    const options = [
      { label: this.anyDateValue, value: null },
      { label: this.todayValue, value: todayIso },
      { label: this.tomorrowValue, value: tomorrowIso }
    ]

    this.dateQuickTarget.innerHTML = options.map((option) => {
      const active = option.value === null ? !selected : selected === option.value
      return `<button type="button" class="hero-search__date-quick-btn${active ? " hero-search__date-quick-btn--active" : ""}" data-date-value="${option.value ?? ""}">${option.label}</button>`
    }).join("")

    this.dateQuickTarget.querySelectorAll(".hero-search__date-quick-btn").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        const value = button.dataset.dateValue
        if (value) {
          this.selectDate(value)
        } else {
          this.setAnyDate()
          this.closeDateDropdown()
        }
      })
    })
  }

  renderCalendar() {
    const year = this.calendarView.getFullYear()
    const month = this.calendarView.getMonth()
    const monthLabel = this.calendarView.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    const weekdays = weekdayLabels()
    const days = calendarDays(year, month)
    const selected = this.dateInputTarget.value
    const todayIso = toIsoDate(new Date())
    const minDate = todayIso

    this.dateCalendarTarget.innerHTML = `
      <div class="hero-search__calendar-nav">
        <button type="button" class="hero-search__calendar-nav-btn" data-calendar-action="prev" aria-label="Previous month">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"/></svg>
        </button>
        <span class="hero-search__calendar-month">${monthLabel}</span>
        <button type="button" class="hero-search__calendar-nav-btn" data-calendar-action="next" aria-label="Next month">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/></svg>
        </button>
      </div>
      <div class="hero-search__calendar-weekdays">
        ${weekdays.map((label) => `<span class="hero-search__calendar-weekday">${label}</span>`).join("")}
      </div>
      <div class="hero-search__calendar-grid">
        ${days.map((day) => {
          if (!day) return `<span class="hero-search__calendar-day hero-search__calendar-day--empty"></span>`
          const iso = toIsoDate(day)
          const disabled = iso < minDate
          const classes = [
            "hero-search__calendar-day",
            iso === todayIso ? "hero-search__calendar-day--today" : "",
            iso === selected ? "hero-search__calendar-day--selected" : "",
            disabled ? "hero-search__calendar-day--disabled" : ""
          ].filter(Boolean).join(" ")
          return `<button type="button" class="${classes}" data-date-value="${iso}" ${disabled ? "disabled" : ""}>${day.getDate()}</button>`
        }).join("")}
      </div>
    `

    this.dateCalendarTarget.querySelector('[data-calendar-action="prev"]')?.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.calendarView = addMonths(this.calendarView, -1)
      this.renderCalendar()
    })

    this.dateCalendarTarget.querySelector('[data-calendar-action="next"]')?.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.calendarView = addMonths(this.calendarView, 1)
      this.renderCalendar()
    })

    this.dateCalendarTarget.querySelectorAll(".hero-search__calendar-day[data-date-value]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.selectDate(button.dataset.dateValue)
      })
    })
  }

  selectDate(isoDate) {
    this.dateInputTarget.disabled = false
    this.dateInputTarget.value = isoDate
    this.dateLabelTarget.textContent = formatDisplayDate(isoDate)
    this.dateClearTarget.classList.remove("hidden")
    this.calendarView = startOfMonth(new Date(`${isoDate}T12:00:00`))
    this.closeDateDropdown()
  }

  renderTimeDropdown() {
    const selected = this.timeInputTarget.value
    const presets = [
      { label: this.anyTimeValue, value: null },
      { label: "07:00", value: "07:00" },
      { label: "12:00", value: "12:00" },
      { label: "18:00", value: "18:00" }
    ]

    this.timeQuickTarget.innerHTML = presets.map((option) => {
      const active = option.value === null ? !selected : selected === option.value
      const label = option.value ? formatDisplayTime(option.value) : option.label
      return `<button type="button" class="hero-search__date-quick-btn${active ? " hero-search__date-quick-btn--active" : ""}" data-time-value="${option.value ?? ""}">${label}</button>`
    }).join("")

    this.timeQuickTarget.querySelectorAll(".hero-search__date-quick-btn").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        const value = button.dataset.timeValue
        if (value) {
          this.selectTime(value)
        } else {
          this.setAnyTime()
          this.closeTimeDropdown()
        }
      })
    })

    const slots = []
    for (let hour = 0; hour <= 23; hour += 1) {
      slots.push(`${String(hour).padStart(2, "0")}:00`)
      slots.push(`${String(hour).padStart(2, "0")}:30`)
    }

    this.timeGridTarget.innerHTML = slots.map((slot) => {
      const active = selected === slot
      return `<button type="button" class="browse-schedule-picker__time-slot${active ? " browse-schedule-picker__time-slot--active" : ""}" data-time-value="${slot}">${formatDisplayTime(slot)}</button>`
    }).join("")

    this.timeGridTarget.querySelectorAll(".browse-schedule-picker__time-slot").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.selectTime(button.dataset.timeValue)
      })
    })
  }

  selectTime(value) {
    this.timeInputTarget.disabled = false
    this.timeInputTarget.value = value
    this.timeLabelTarget.textContent = formatDisplayTime(value)
    this.timeClearTarget.classList.remove("hidden")
    this.closeTimeDropdown()
  }

  startOfMonthFromInput() {
    const value = this.dateInputTarget.value
    if (value) return startOfMonth(new Date(`${value}T12:00:00`))
    return startOfMonth(new Date())
  }
}
